import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Film, Loader2, Mail, RefreshCw, Trash2, UploadCloud } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK = 8 * 1024 * 1024;
const ACCEPT = "video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm";

const sizeText = (b) => (b > 1e9 ? `${(b / 1e9).toFixed(2)} GB` : `${Math.round(b / 1e6)} MB`);
const newUploadId = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (x) => x.toString(16).padStart(2, "0")).join("");

async function sendChunk(base, uploadId, index, blob, onProgress) {
  for (let attempt = 0; ; attempt++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(index));
    fd.append("file", blob, "chunk");
    try {
      return await axios.post(`${base}/film/chunk`, fd, { onUploadProgress: (e) => onProgress(e.loaded) });
    } catch (err) {
      const status = err?.response?.status;
      if (attempt >= 3 || (status && status < 500)) throw err;
      await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
    }
  }
}

export default function FilmSection({ token, film, familyEmail, onChange }) {
  const base = `${API}/owner/${token}`;
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["mp4", "m4v", "mov", "webm"].includes(ext)) {
      toast.error("Please choose an MP4, MOV, M4V or WEBM video.");
      return;
    }
    if (file.size > 4 * 1024 ** 3) {
      toast.error("That film is over 4 GB. Please export a smaller file.");
      return;
    }
    if (film?.sent_at && !window.confirm("The family already has this film. Their link will pause until you email them the new version. Replace it?")) return;
    const uploadId = newUploadId();
    const total = Math.ceil(file.size / CHUNK);
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const start = i * CHUNK;
        await sendChunk(base, uploadId, i, file.slice(start, start + CHUNK),
                        (loaded) => setProgress(Math.min(99, Math.round(((start + loaded) / file.size) * 100))));
      }
      setSaving(true);
      await axios.post(`${base}/film/complete`, { upload_id: uploadId, filename: file.name });
      // Joining and saving runs on the server in the background; check until it's done
      const deadline = Date.now() + 45 * 60 * 1000;
      let film = null;
      while (!film) {
        if (Date.now() > deadline) throw new Error("timeout");
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await axios.get(`${base}/film/upload/${uploadId}`).catch((e) => {
          if (e?.response?.status === 404) return { data: { status: "failed" } };
          return { data: { status: "processing" } }; // brief network hiccup: keep waiting
        });
        if (data.status === "failed") throw new Error("failed");
        if (data.status === "done") film = data.film;
      }
      onChange(film);
      toast.success("Film uploaded. Watch it below, then send it to the family.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The upload didn't finish. Please try again.");
    } finally {
      setProgress(null);
      setSaving(false);
    }
  };

  const send = async () => {
    const again = film.sent_at ? " again" : "";
    if (!window.confirm(`Email ${familyEmail} the private link to watch and download this film${again}?`)) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${base}/film/send`);
      onChange(data);
      toast.success("The family has been emailed their film link.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this film? The family will no longer be able to watch it.")) return;
    await axios.delete(`${base}/film`).then(() => onChange(null)).catch(() => toast.error("Could not remove the film."));
  };

  const busy = progress !== null || saving;
  const picker = (
    <input ref={inputRef} data-testid="owner-film-file-input" type="file" accept={ACCEPT} className="hidden"
           onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
  );

  return (
    <section data-testid="owner-film-section" className="mt-8 card-surface p-6">
      <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#C5A55A]/80"><Film size={13} /> Finished tribute film</div>
      {picker}
      {busy ? (
        <div data-testid="owner-film-progress" className="mt-4">
          <div className="flex justify-between text-sm text-[#F5F0E8]"><span>{saving ? "Saving the film safely… big films can take a few minutes. Please keep this page open." : "Uploading film…"}</span><span>{saving ? "" : `${progress}%`}</span></div>
          <div className="mt-2 h-1.5 rounded-full bg-[rgba(245,240,232,0.08)] overflow-hidden">
            <div className="h-full bg-[#D4AF37] transition-[width] duration-300" style={{ width: `${saving ? 100 : progress}%` }} />
          </div>
          <p className="mt-2 text-xs text-[#A0988E]">Keep this page open until it finishes.</p>
        </div>
      ) : !film ? (
        <button data-testid="owner-film-upload-button" onClick={() => inputRef.current?.click()}
                className="mt-4 w-full rounded-2xl border-2 border-dashed border-[rgba(197,165,90,0.3)] hover:border-[rgba(212,175,55,0.6)] py-10 text-center transition-colors">
          <UploadCloud size={24} className="mx-auto text-[#D4AF37]" strokeWidth={1.4} />
          <div className="serif text-xl text-[#F5F0E8] mt-3">Upload the finished film</div>
          <div className="text-xs text-[#A0988E] mt-1">MP4 or MOV · up to 4 GB</div>
        </button>
      ) : (
        <div className="mt-4 grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-6 items-start">
          <video data-testid="owner-film-preview" src={`${base}/film/stream`} controls preload="metadata" className="w-full rounded-xl bg-black aspect-video" />
          <div>
            <div data-testid="owner-film-name" className="text-[#F5F0E8] break-words">{film.filename}</div>
            <div className="text-xs text-[#A0988E] mt-1">{sizeText(film.size)} · uploaded {new Date(film.uploaded_at).toLocaleString()}</div>
            <div data-testid="owner-film-status" className="mt-4 text-sm flex items-center gap-2 text-[#F5F0E8]">
              {film.sent_at ? <><CheckCircle2 size={15} className="text-[#D4AF37]" /> Sent to the family {new Date(film.sent_at).toLocaleString()}</> : "Not sent to the family yet"}
            </div>
            <button data-testid="owner-film-send-button" onClick={send} disabled={sending} className="btn-primary mt-5 w-full justify-center disabled:opacity-60">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} {film.sent_at ? "Email the family again" : "Mark done & email the family"}
            </button>
            <div className="mt-3 flex gap-4 text-xs">
              <button data-testid="owner-film-replace-button" onClick={() => inputRef.current?.click()} className="text-[#A0988E] hover:text-[#F5F0E8] inline-flex items-center gap-1"><RefreshCw size={12} /> Replace film</button>
              <button data-testid="owner-film-remove-button" onClick={remove} className="text-[#A0988E] hover:text-[#E6B89C] inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
